/**
 * @file reportingService.js
 * @description API helpers for report run definitions, create, history, details, download, and delete.
 */

import { format } from 'date-fns';
import { clientApiHttp } from './httpService';

const getTenantId = () => +window.localStorage.getItem('tenantId');

function resolveTenantId(tenantId) {
    if (tenantId) {
        return tenantId;
    }

    return getTenantId();
}

function buildGeneratedReportsBasePath(tenantId) {
    return `/v2/client/${resolveTenantId(tenantId)}/reporting/reports`;
}

function buildReportFilename(reportName, createdAt, extension) {
    const sanitized = String(reportName || 'report')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

    const dateStamp = createdAt
        ? format(new Date(createdAt), 'yyyyMMdd')
        : format(new Date(), 'yyyyMMdd');

    return `${sanitized}_${dateStamp}.${extension}`;
}

function downloadBlob(blob, filename) {
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    window.URL.revokeObjectURL(objectUrl);
}

export async function fetchReportDefinitions(tenantId) {
    const basePath = buildGeneratedReportsBasePath(tenantId);
    const response = await clientApiHttp.get(`${basePath}/definitions`);
    return response?.data?.definitions || [];
}

export async function createGeneratedReport(payload, tenantId) {
    const basePath = buildGeneratedReportsBasePath(tenantId);
    const response = await clientApiHttp.post(basePath, payload);
    return response?.data || null;
}

export async function fetchReportHistory(params = {}, tenantId) {
    const basePath = buildGeneratedReportsBasePath(tenantId);
    const searchParams = new URLSearchParams();

    if (params?.skip !== undefined && params?.skip !== null) {
        searchParams.append('skip', String(params.skip));
    }

    if (params?.take !== undefined && params?.take !== null) {
        searchParams.append('take', String(params.take));
    }

    const queryString = searchParams.toString();
    const response = await clientApiHttp.get(
        queryString ? `${basePath}?${queryString}` : basePath,
    );
    return (
        response?.data || { myReports: [], sharedWithMe: [], allReports: [] }
    );
}

export async function fetchGeneratedReportById(generatedReportId, tenantId) {
    const basePath = buildGeneratedReportsBasePath(tenantId);
    const response = await clientApiHttp.get(
        `${basePath}/${generatedReportId}`,
    );
    return response?.data || null;
}

export async function downloadGeneratedReportFile(
    generatedReportId,
    outputFormat,
    { reportName, createdAt } = {},
    tenantId,
) {
    const basePath = buildGeneratedReportsBasePath(tenantId);
    const queryString = outputFormat
        ? `?outputFormat=${encodeURIComponent(outputFormat)}`
        : '';

    const response = await clientApiHttp.get(
        `${basePath}/${generatedReportId}/download${queryString}`,
        {
            responseType: 'blob',
        },
    );

    const extension = outputFormat === 'excel' ? 'xlsx' : 'csv';
    const filename = buildReportFilename(reportName, createdAt, extension);

    downloadBlob(response.data, filename);
}

export async function fetchGeneratedReportCsv(generatedReportId, tenantId) {
    const basePath = buildGeneratedReportsBasePath(tenantId);
    const response = await clientApiHttp.get(
        `${basePath}/${generatedReportId}/download?outputFormat=csv`,
        { responseType: 'text' },
    );
    return response?.data || '';
}

export async function deleteGeneratedReport(
    generatedReportId,
    tenantId,
    forceDelete = false,
) {
    const basePath = buildGeneratedReportsBasePath(tenantId);
    const response = await clientApiHttp.delete(
        `${basePath}/${generatedReportId}${forceDelete ? '?forceDelete=true' : ''}`,
    );
    return response?.data;
}
